import { IFramework, IWebServiceManager, HttpMethod } from '@truckdown/framework';
import { IDataLogger } from './IDataLogger';
import { SystemInfoModel } from './SystemInfoModel';
import { LogEntryModel } from './LogEntryModel';
import { UserContextClientModel, UserContextModel, UserContextSystemModel, UserContextUserModel } from './UserContextModel';
import { LogEntryEventIdModel } from './LogEntryEventIdModel';
import { LogEntryExceptionModel } from './LogEntryExceptionModel';

class DataLogger implements IDataLogger {
    constructor(framework: IFramework) {
        this.manager = framework.getRequiredService<IWebServiceManager>('IWebServiceManager');
    }

    readonly manager: IWebServiceManager;

    private getSystemModel(model: SystemInfoModel): any {
        if (!model || model === null) {
            return null;
        }

        return {
            _id: model.id,
            env: model.environment,
            dt: model.date,
            hb: model.heartBeat,
            n: model.name,
            pc: model.processorCount,
            p: model.platform,
            pv: model.platformVersion,
            title: model.title,
            t: model.type,
            prod: model.product,
            v: model.version,
            av: model.assemblyVersion,
            desc: model.description,
            comp: model.company,
            copy: model.copyright,
            frame: model.framework,
            conf: model.config,
            ref: model.references
        };
    }

    private getContextModel(model: UserContextModel): any {
        if (!model || model === null) {
            return null;
        }

        return {
            _id: model.id,
            instance: model.instance,
            ir: model.input,
            start: model.start,
            client: model.client,
            system: model.system,
            user: model.user,
            status: model.status,
            settings: model.settings
        }
    }

    private getEventModel(model: LogEntryEventIdModel | null): any {
        if (!model || model === null) {
            return null;
        }

        return {
            id: model.id,
            n: model.name
        };
    }

    private getExceptionModel(model: LogEntryExceptionModel | null): any {
        if (!model || model === null) {
            return null;
        }

        return {
            msg: model.message,
            s: model.source,
            st: model.stackTrace,
            ts: model.targetSite,
            hl: model.helpLink,
            innerException: this.getExceptionModel(model.innerException)
        };
    }

    private getLogModel(model: LogEntryModel): any {
        if (!model || model === null) {
            return null;
        }

        return {
            _id: model.id,
            t: model.timeStamp,
            eid: this.getEventModel(model.eventId),
            l: model.level,
            c: model.category,
            msg: model.message,
            inst: model.instance,
            contextId: model.contextId,
            uid: model.userId,
            ex: this.getExceptionModel(model.exception),
            props: model.properties
        };
    }

    logSystem(model: SystemInfoModel): Promise<undefined> {
        let req = this.manager.createRequest({
            service: this.manager.api,
            path: '/logging/v3/log-app',
            method: HttpMethod.PUT
        }, this.getSystemModel(model));

        return new Promise<undefined>((resolve, reject) => {
            this.manager
                .send(req)
                .then((res) => {
                    if (res.ok) {
                        resolve(undefined);
                    } else {
                        reject(res.statusText);
                    }
                })
                .catch((err) => {
                    console.log(err);
                    reject(err);
                })
        });
    }
    logContext(model: UserContextModel): Promise<undefined> {
        let req = this.manager.createRequest({
            service: this.manager.api,
            path: '/logging/v3/log-context',
            method: HttpMethod.PUT
        }, this.getContextModel(model));

        return new Promise<undefined>((resolve, reject) => {
            this.manager
                .send(req)
                .then((res) => {
                    if (res.ok) {
                        resolve(undefined);
                    } else {
                        reject(res.statusText);
                    }
                })
                .catch((err) => {
                    console.log(err);
                    reject(err);
                })
        });

    }
    logEntry(model: LogEntryModel): Promise<undefined> {
        let req = this.manager.createRequest({
            service: this.manager.api,
            path: '/logging/v3/add-entry',
            method: HttpMethod.PUT
        }, this.getLogModel(model));

        return new Promise<undefined>((resolve, reject) => {
            this.manager
                .send(req)
                .then((res) => {
                    if (res.ok) {
                        resolve(undefined);
                    } else {
                        reject(res.statusText);
                    }
                })
                .catch((err) => {
                    console.log(err);
                    reject(err);
                })
        });
    }

    logStatus(contextId: string, status: number): Promise<undefined> {
        let req = this.manager.createRequest({
            service: this.manager.api,
            path: '/logging/v3/update-status/' + encodeURIComponent(contextId) + '/' + status,
            method: HttpMethod.PUT
        }, {});

        return new Promise<undefined>((resolve, reject) => {
            this.manager
                .send(req)
                .then((res) => {
                    if (res.ok) {
                        resolve(undefined);
                    } else {
                        reject(res.statusText);
                    }
                })
                .catch((err) => {
                    console.log(err);
                    reject(err);
                })
        });
    }
    heartBeat(instance: string): Promise<undefined> {
        let req = this.manager.createRequest({
            service: this.manager.api,
            path: '/logging/v3/heartbeat/' + encodeURIComponent(instance),
            method: HttpMethod.PUT
        }, {});

        return new Promise<undefined>((resolve, reject) => {
            this.manager
                .send(req)
                .then((res) => {
                    if (res.ok) {
                        resolve(undefined);
                    } else {
                        reject(res.statusText);
                    }
                })
                .catch((err) => {
                    console.log(err);
                    reject(err);
                })
        });
    }
}


export default (framework: IFramework) => {
    framework.registerTransient<IDataLogger>('IDataLogger', (frm) => {
        return new DataLogger(frm);
    });
}